import { round } from "@condo/helpers";
import { capitalCase, sentenceCase } from "change-case";
import dayjs from "dayjs";
import localeDe from "dayjs/locale/de.js";
import dayjsTimezone from "dayjs/plugin/timezone.js";
import dayjsUtc from "dayjs/plugin/utc.js";
import { groupBy, isNil, isPlainObject, omit } from "lodash";
import { Decimal } from "decimal.js";
//#endregion
//#region src/formatters.ts
dayjs.extend(dayjsUtc);
dayjs.extend(dayjsTimezone);
const DEFAULT_NUMBER_LOCALE = "de-DE";
const compactNumberFormat = (value, locale = DEFAULT_NUMBER_LOCALE) => new Intl.NumberFormat(locale, {
	notation: "compact",
	maximumFractionDigits: 1
}).format(value);
const localNumberFormat = (value, minimumFractionDigits = 2, maximumFractionDigits = 2, locale = DEFAULT_NUMBER_LOCALE) => {
	if (isNil(value)) return value;
	const formatter = new Intl.NumberFormat(locale, {
		minimumFractionDigits,
		maximumFractionDigits
	});
	if (typeof value !== "number" && !Array.isArray(value)) return value;
	return Array.isArray(value) ? value.map((item) => formatter.format(item)) : formatter.format(value);
};
const squareMeter = (value, locale = DEFAULT_NUMBER_LOCALE) => {
	if (isNil(value)) return "";
	const rounded = round(Number(value), 2);
	return `${localNumberFormat(rounded, 0, 2, locale)} m²`;
};
const cubicMeter = (value) => {
	if (isNil(value)) return "";
	const rounded = round(Number(value), 2);
	return `${localNumberFormat(rounded, 0, 2)} m³`;
};
const kWhPerSqmYear = (value, locale = DEFAULT_NUMBER_LOCALE) => `${localNumberFormat(round(value, 2), 2, 2, locale)} kWh/m²*a`;
const kWhPerYear = (value) => `${localNumberFormat(round(value, 2), 2, 2)} kWh/a`;
const currency = (value, minimumFractionDigits = 2, maximumFractionDigits = 2, currency = "EUR", formatNilAsZero = true, locale = DEFAULT_NUMBER_LOCALE) => {
	const formatter = new Intl.NumberFormat(locale, {
		style: "currency",
		currency,
		minimumFractionDigits,
		maximumFractionDigits
	});
	if (isNil(value)) return formatNilAsZero ? formatter.format(0) : "";
	if (typeof value === "string") return formatter.format(Number(value));
	if (value instanceof Decimal) return formatter.format(value.toNumber());
	if (typeof value === "number") return formatter.format(value);
	if (!(typeof value === "number" || value instanceof Decimal) && !Array.isArray(value)) return value;
	return value.map((item) => formatter.format(item instanceof Decimal ? item.toNumber() : item));
};
const currencyOrEmpty = (value, minimumFractionDigits = 2, maximumFractionDigits = 2, currencyValue = "EUR") => {
	if (isNil(value)) return "";
	return currency(value, minimumFractionDigits, maximumFractionDigits, currencyValue);
};
const perSquareMeter = (value) => Array.isArray(value) ? value.map((item) => `${item}/m²`) : `${value}/m²`;
const currencyPerSquareMeter = (value, fallback = "", currencyFn = currency) => {
	if (isNil(value)) return fallback;
	return perSquareMeter(currencyFn(value));
};
/** German typography separates the percent sign (`16,3 %`); English keeps it attached (`16.3%`). */
const percentSuffix = (locale) => locale.startsWith("de") ? " %" : "%";
const toPercent = (item, precision, locale) => {
	const numericValue = (item instanceof Decimal ? item.toNumber() : item) * 100;
	return `${localNumberFormat(numericValue, precision, precision, locale)}${percentSuffix(locale)}`;
};
const percent = (value, precision = 2, locale = DEFAULT_NUMBER_LOCALE) => {
	if (isNil(value)) return "";
	if (!(typeof value === "number" || value instanceof Decimal) && !Array.isArray(value)) return value;
	return Array.isArray(value) ? value.map((item) => toPercent(item, precision, locale)) : toPercent(value, precision, locale);
};
const scaledPercentage = (value, maximumFractionDigits = 0, minimumFractionDigits = 0) => {
	return percentage(value + 1, maximumFractionDigits, minimumFractionDigits);
};
const percentage = (value, maximumFractionDigits = 0, minimumFractionDigits = 0, formatNilAsZero = true) => {
	if (isNil(value)) return formatNilAsZero ? new Intl.NumberFormat("de-DE", {
		style: "percent",
		minimumFractionDigits,
		maximumFractionDigits
	}).format(0) : "";
	return new Intl.NumberFormat("de-DE", {
		style: "percent",
		minimumFractionDigits,
		maximumFractionDigits
	}).format(value);
};
const multiple = (value, locale = DEFAULT_NUMBER_LOCALE) => {
	if (typeof value === "number") return `${localNumberFormat(value, 0, 2, locale)}×`;
	return `${value}×`;
};
const meters = (value, locale = DEFAULT_NUMBER_LOCALE) => {
	if (!value) return "";
	if (typeof value === "number") return `${localNumberFormat(value, 0, 2, locale)} m`;
	return `${value} m`;
};
const METERS_PER_KILOMETER = 1e3;
const distanceFromMeters = (value, kilometerThresholdMeters = METERS_PER_KILOMETER, locale = DEFAULT_NUMBER_LOCALE) => {
	if (isNil(value)) return "";
	if (value >= kilometerThresholdMeters) return `${localNumberFormat(value / METERS_PER_KILOMETER, 0, 1, locale)} km`;
	return meters(localNumberFormat(value, 0, 0, locale), locale);
};
const minutes = (value) => value ? `${value} min` : "";
const delta = (from, to, locale = DEFAULT_NUMBER_LOCALE) => {
	if (from === null || to === null) return null;
	let res = to / from - 1;
	if (from < 0) res *= -1;
	return percent(res, 2, locale);
};
const deltaV2 = (from, to) => {
	if (from === null || to === null) return null;
	let result;
	if (from >= 0) result = round(to / from - 1, 4);
	else result = round((to / from - 1) * -1, 4);
	return percent(result, 2);
};
const toUpperCase = (str) => str?.toUpperCase?.() ?? str;
const addressFormatter = (location = {}, ignoredProps) => {
	if (isNil(location)) return "";
	let streetPart = "";
	let zipcodePart = "";
	let cityQuarterPart = "";
	let countryPart = "";
	const { street, houseNumber, city, quarter, zipcode, country, addressLine2 } = ignoredProps ? omit(location, ignoredProps) : location;
	if (street) streetPart = `${street}${houseNumber ? ` ${String(houseNumber).trim()}` : ""}`;
	if (zipcode) zipcodePart = zipcode;
	if (city) cityQuarterPart = `${city}${quarter ? ` - ${quarter}` : ""}`;
	else if (quarter) cityQuarterPart = quarter;
	if (country) countryPart = `, ${country}`;
	return [
		streetPart,
		addressLine2,
		[zipcodePart, `${cityQuarterPart}${countryPart}`].filter((s) => s).join(" ")
	].filter((s) => s).join(", ");
};
/**
* Formats a location to the Looker Asset filter value format.
* Expected format: "{street} {houseNumber},  {city}" (comma + two spaces before city).
*/
const lookerAssetFilterFormatter = (location) => {
	if (isNil(location) || !location.street && !location.houseNumber && !location.city) return "";
	const streetPart = `${location.street ?? ""}${location.houseNumber ? ` ${String(location.houseNumber).trim()}` : ""}`.trim();
	const cityPart = location.city ?? "";
	if (!streetPart || !cityPart) return streetPart || cityPart;
	return `${streetPart}‚  ${cityPart}`;
};
const mfhBuildingAddressesFormatter = (locations) => {
	if (!locations?.length) return [];
	let zipcodePart = "";
	let cityQuarterPart = "";
	let countryPart = "";
	const groupedLocations = groupBy(locations, "street");
	return Object.values(groupedLocations).map((locations) => {
		const { city, country, quarter, zipcode } = locations[0];
		locations = locations.sort((a, b) => +a.houseNumber - +b.houseNumber);
		let streetPart = locations[0].street ? `${locations[0].street} ` : "";
		streetPart += locations.map((location) => `${location.houseNumber ? String(location.houseNumber).trim() : ""}`).join(", ");
		if (zipcode) zipcodePart = zipcode;
		if (city) cityQuarterPart = `${city}${quarter ? ` - ${quarter}` : ""}`;
		else if (quarter) cityQuarterPart = quarter;
		if (country) countryPart = `, ${country}`;
		return [streetPart, [zipcodePart, `${cityQuarterPart}${countryPart}`].filter((s) => s).join(" ")].filter((s) => s).join(", ");
	});
};
const addressFormatterWithFloor = (location = {}, attributes = {}, ignoredProps) => {
	if (isNil(location)) return "";
	let streetPart = "";
	let zipcodePart = "";
	let cityQuarterPart = "";
	let countryPart = "";
	let floorPart = "";
	const { street, houseNumber, city, quarter, zipcode, country } = ignoredProps ? omit(location, ignoredProps) : location;
	if (street) streetPart = `${street}${houseNumber ? ` ${String(houseNumber).trim()}` : ""}`;
	if (zipcode) zipcodePart = zipcode;
	if (city) cityQuarterPart = `${city}${quarter ? ` - ${quarter}` : ""}`;
	else if (quarter) cityQuarterPart = quarter;
	if (country) countryPart = `, ${country}`;
	if (attributes.floor && attributes.floorLocation) floorPart = `${attributes.floor}. OG, ${attributes.floorLocation}`;
	return [streetPart, [
		floorPart,
		zipcodePart,
		`${cityQuarterPart}${countryPart}`
	].filter((s) => s).join(" ")].filter((s) => s).join(", ");
};
const bankDetailsFormatter = (obj = {}) => {
	if (!Object.keys(obj).length) return "[not-specified]";
	return `Bank: ${obj.bank?.name ?? "(no)"}. IBAN: ${obj.iban ?? "(no)"} BIC: ${obj.bic ?? "(no)"}`;
};
const formatFloor = (obj = {}) => {
	if (isNil(obj)) return "";
	const floorString = [];
	const { floor, shouldAbbreviateFloorDesignation = true, floorLocation } = obj;
	if (!isNil(floor)) {
		const floorNum = Number(floor);
		if (floorNum === 0) floorString.push(shouldAbbreviateFloorDesignation ? "EG" : "Erdgeschoss");
		else if (floorNum < 0) floorString.push(`${floorNum}. ${shouldAbbreviateFloorDesignation ? "UG" : "Untergeschoss"}`);
		else if (floorNum >= 100) floorString.push(`${floorNum % 100}. ${shouldAbbreviateFloorDesignation ? "DG" : "Dachgeschoss"}`);
		else floorString.push(`${floorNum}. ${shouldAbbreviateFloorDesignation ? "OG" : "Obergeschoss"}`);
	}
	if (floorLocation) floorString.push(floorLocation);
	return floorString.filter((s) => s).join(", ");
};
const intlDateFormat = (val, withTime, month = "2-digit", year = "2-digit") => {
	if (isNil(val)) return "";
	const options = {
		year,
		month,
		day: "2-digit"
	};
	if (withTime) {
		options.hour = "numeric";
		options.minute = "numeric";
	}
	if (typeof val === "string") val = new Date(val);
	return new Intl.DateTimeFormat("de-DE", options).format(val);
};
const docDateFormat = (val, withTime, isShort) => {
	if (!val) return "";
	if (typeof val === "string") val = new Date(val);
	let timeFormat = "";
	if (withTime) timeFormat = " HH:mm:ss";
	return dayjs.utc(val).tz("Europe/Berlin").locale(localeDe).format(`DD.${isShort ? "MM." : " MMMM "}YYYY${timeFormat}`);
};
const docTimeFormat = (val) => dayjs.utc(val).tz("Europe/Berlin").locale(localeDe).format("HH:mm:ss");
const bpsToPercent = (value, maximumFractionDigits = 2, minimumFractionDigits = 0) => {
	if (!value) return percentage(0, maximumFractionDigits, minimumFractionDigits);
	return percentage(Number.parseInt(value, 10) / 1e4, maximumFractionDigits, minimumFractionDigits);
};
const iban = (val) => {
	if (!val) return "";
	return val.replace(/\s+/g, "").split(/(.{4})/).filter((s) => s).join(" ");
};
const taxIdFormat = (val) => {
	if (!val) return "";
	return val.replace(/s+/g, "").replace(/s+/g, "").split(/(.{2})(.{3})(.{3})(.{3})/).filter((s) => s).join(" ");
};
const booleanYesNoFormatter = (obj) => {
	const val = isPlainObject(obj) && "value" in obj ? obj.value : obj;
	return val === false ? "No" : val === true ? "Yes" : val;
};
const booleanTrueFalseFormatter = (obj) => {
	const val = isPlainObject(obj) && "value" in obj ? obj.value : obj;
	return val === false ? "False" : val === true ? "True" : val;
};
const booleanGermanYesNoFormatter = (obj) => {
	const val = isPlainObject(obj) && "value" in obj ? obj.value : obj;
	return val === false ? "Nein" : val === true ? "Ja" : val;
};
const emptyOrOtherFormatter = (otherFormatter, ...otherArgs) => (obj) => {
	const value = isPlainObject(obj) && "value" in obj ? obj.value : obj;
	if (typeof value === "undefined" || value === null) return "";
	return otherFormatter(value, ...otherArgs);
};
const toTitleCase = (val) => val ? capitalCase(sentenceCase(val)) : "";
const formatPhoneNumber = (val, countryCode) => {
	const hasCode = val.startsWith("+");
	const phone = val.replace(/\D/g, "").replace(/^0/, "");
	return (hasCode ? "+" : `+${countryCode.replace("+", "")}`) + phone;
};
const formatFactor = (value, locale = DEFAULT_NUMBER_LOCALE) => !isNil(value) ? `${localNumberFormat(value, 0, 2, locale)} ×` : "";
const createLocaleNumberFormatters = (locale) => {
	const boundCurrency = (value, minimumFractionDigits = 2, maximumFractionDigits = 2, currencyCode = "EUR", formatNilAsZero = true) => currency(value, minimumFractionDigits, maximumFractionDigits, currencyCode, formatNilAsZero, locale);
	return {
		locale,
		localNumberFormat: (value, minimumFractionDigits = 2, maximumFractionDigits = 2) => localNumberFormat(value, minimumFractionDigits, maximumFractionDigits, locale),
		currency: boundCurrency,
		currencyPerSquareMeter: (value, fallback = "", currencyFn = boundCurrency) => currencyPerSquareMeter(value, fallback, currencyFn),
		percent: (value, precision = 2) => percent(value, precision, locale),
		squareMeter: (value) => squareMeter(value, locale),
		multiple: (value) => multiple(value, locale),
		meters: (value) => meters(value, locale),
		distanceFromMeters: (value, kilometerThresholdMeters = METERS_PER_KILOMETER) => distanceFromMeters(value, kilometerThresholdMeters, locale),
		formatFactor: (value) => formatFactor(value, locale),
		kWhPerSqmYear: (value) => kWhPerSqmYear(value, locale),
		delta: (from, to) => delta(from, to, locale)
	};
};
const formatFileSize = (bytes) => {
	if (!bytes) return "";
	const sizes = [
		"B",
		"KB",
		"MB",
		"GB"
	];
	const i = Math.floor(Math.log(bytes) / Math.log(1024));
	return `${Math.round(bytes / 1024 ** i * 100) / 100} ${sizes[i]}`;
};
const SheetFormatters = {
	date: { numberFormat: {
		type: "DATE",
		pattern: "DD/MM/YYYY"
	} },
	euroCurrency: { numberFormat: {
		type: "CURRENCY",
		pattern: "#,##0.00€"
	} },
	meterSquared: { numberFormat: {
		type: "TEXT",
		pattern: "#,##0.00 \"m²\""
	} },
	percentage: { numberFormat: {
		type: "PERCENT",
		pattern: "#0.00%"
	} },
	booleanCheckbox: { checkbox: true }
};
const stateFormatter = (value) => {
	if (value === "Hamburg") return "HH";
	return value ?? "";
};
const formatDate = (value, format = "YYYY-MM-DD") => {
	if (!value) return null;
	const date = dayjs(value);
	if (!date.isValid()) return null;
	return date.format(format);
};
const formatDateArray = (values, format = "YYYY-MM-DD") => {
	if (!values) return [];
	return values.map((v) => formatDate(v, format));
};
const extractYearFromDate = (value) => {
	if (!value) return null;
	try {
		const date = typeof value === "string" ? dayjs(value) : dayjs(value);
		return date.isValid() ? date.year() : null;
	} catch {
		return null;
	}
};
const extractYearsFromDateArray = (values) => {
	if (!values) return [];
	return values.map(extractYearFromDate);
};
const mapArrayToYesNo = (values) => {
	if (!values) return;
	return values.map((value) => value === true ? "Ja" : value === false ? "Nein" : "");
};
const mapArrayWithLookup = (mapping) => {
	return (values) => {
		if (!values) return [];
		return values.map((value) => value != null && value in mapping ? mapping[value] : null);
	};
};
const spreadsheetDateToJsDate = (excelDate) => {
	if (excelDate === null || typeof excelDate !== "number") return null;
	const milliseconds = (excelDate - 25569) * (1e3 * 60 * 60 * 24);
	return new Date(milliseconds);
};
const spreadsheetDateArrayToJsDates = (format, values) => {
	if (!Array.isArray(values)) return [];
	return values.map((value) => formatDate(spreadsheetDateToJsDate(value), format));
};
//#endregion
export { DEFAULT_NUMBER_LOCALE, SheetFormatters, addressFormatter, addressFormatterWithFloor, bankDetailsFormatter, booleanGermanYesNoFormatter, booleanTrueFalseFormatter, booleanYesNoFormatter, bpsToPercent, compactNumberFormat, createLocaleNumberFormatters, cubicMeter, currency, currencyOrEmpty, currencyPerSquareMeter, delta, deltaV2, distanceFromMeters, docDateFormat, docTimeFormat, emptyOrOtherFormatter, extractYearFromDate, extractYearsFromDateArray, formatDate, formatDateArray, formatFactor, formatFileSize, formatFloor, formatPhoneNumber, iban, intlDateFormat, kWhPerSqmYear, kWhPerYear, localNumberFormat, lookerAssetFilterFormatter, mapArrayToYesNo, mapArrayWithLookup, meters, mfhBuildingAddressesFormatter, minutes, multiple, perSquareMeter, percent, percentage, scaledPercentage, spreadsheetDateArrayToJsDates, spreadsheetDateToJsDate, squareMeter, stateFormatter, taxIdFormat, toTitleCase, toUpperCase };

//# sourceMappingURL=index.mjs.map